import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { MaskedInput } from "components/inputs";
import Tooltip from "components/Tooltip/Tooltip";
import React, { useEffect, useState } from "react";
import type { Control, UseFormGetValues } from "react-hook-form";

import SectionBreak from "../../../components/SectionBreak";
import type { LienFormValues } from "../LienTypes";

type TPOCFieldName = keyof Pick<
  LienFormValues,
  | "tpoc_amount_1"
  | "tpoc_amount_2"
  | "tpoc_amount_3"
  | "tpoc_amount_4"
  | "tpoc_amount_5"
  | "tpoc_date_1"
  | "tpoc_date_2"
  | "tpoc_date_3"
  | "tpoc_date_4"
  | "tpoc_date_5"
>;

type TPOCFields = {
  [K in TPOCFieldName]: string;
};

type TPOCSectionProps = {
  control: Control<TPOCFields>;
  getValues: UseFormGetValues<TPOCFields>;
  onSave: (field: string | null, value: unknown) => Promise<void>;
};

const getTpocFieldName = (
  type: "amount" | "date",
  index: number,
): TPOCFieldName => {
  return `tpoc_${type}_${index}` as TPOCFieldName;
};

export const TPOCSection = ({
  control,
  getValues,
  onSave,
}: TPOCSectionProps) => {
  const [visibleTPOCs, setVisibleTPOCs] = useState(1);

  // render the highest used TPOC (ie if there are 3 filled out, render 3)
  useEffect(() => {
    const maxTPOC = 5;
    let highestUsed = 1;

    for (let i = maxTPOC; i > 0; i--) {
      const amount = getValues(getTpocFieldName("amount", i));
      const date = getValues(getTpocFieldName("date", i));
      if (amount || date) {
        highestUsed = i;
        break;
      }
    }

    setVisibleTPOCs(highestUsed);
  }, [getValues]);

  // clear both the amount and date when deleting a TPOC
  const handleTPOCDelete = async (index: number) => {
    await onSave(null, {
      [getTpocFieldName("amount", index)]: null,
      [getTpocFieldName("date", index)]: null,
    });
    setVisibleTPOCs(index - 1);
  };

  const isTPOCFilled = (index: number) => {
    const amount = getValues(getTpocFieldName("amount", index));
    const date = getValues(getTpocFieldName("date", index));
    return Boolean(amount && date);
  };

  const renderTPOCFields = (index: number) => (
    <>
      <ControlledInput
        control={control}
        name={getTpocFieldName("amount", index)}
        label={`TPOC Amount ${index}`}
        errors={{}}
        onSave={(value) => onSave(getTpocFieldName("amount", index), value)}
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledDateInput
        control={control}
        name={getTpocFieldName("date", index)}
        label={`TPOC Date ${index}`}
        errors={{}}
        onSave={(value) => onSave(getTpocFieldName("date", index), value)}
      />
    </>
  );

  return (
    <>
      <SectionBreak />
      <div className="header-primary flex jc-sb ai-c">
        Total payment obligations to the claimant (TPOCS)
        <div className="flex gap-sm jc-end">
          {visibleTPOCs > 1 && (
            <ButtonIcon
              kind="neutral"
              size="small"
              className="delete"
              onClick={() => handleTPOCDelete(visibleTPOCs)}
              type="button"
            >
              delete
            </ButtonIcon>
          )}
          <Tooltip
            content={
              visibleTPOCs >= 5
                ? "Maximum of 5 TPOCs allowed"
                : !isTPOCFilled(visibleTPOCs)
                  ? "Complete all TPOC fields before adding more"
                  : "Add new TPOC"
            }
          >
            <ButtonIcon
              kind="neutral"
              size="small"
              onClick={() => setVisibleTPOCs(Math.min(visibleTPOCs + 1, 5))}
              type="button"
              // disable the button if the max is reached or if both the current TPOC amount and date are not filled out
              disabled={visibleTPOCs >= 5 || !isTPOCFilled(visibleTPOCs)}
            >
              add_circle
            </ButtonIcon>
          </Tooltip>
        </div>
      </div>
      {Array.from({ length: visibleTPOCs }, (_, i) => i + 1).map((index) => (
        <React.Fragment key={index}>{renderTPOCFields(index)}</React.Fragment>
      ))}
    </>
  );
};
